import React, { Suspense, lazy, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Outlet } from 'react-router-dom';
import { QueryClientProvider } from '@tanstack/react-query';
import { queryClient } from './lib/react-query';
import { useUIStore } from './stores';
import { apiService } from './services';
import { env } from './shared/config';

const LoginPage = lazy(() => import('./features/auth/pages/LoginPage'));
const AccountPage = lazy(() => import('./features/account/pages/AccountPage'));
const SettingsPage = lazy(() => import('./features/settings/pages/SettingsPage'));
const NotificationsPage = lazy(() => import('./features/notifications/pages/NotificationsPage'));
const NotFoundPage = lazy(() => import('./features/errors/pages/NotFoundPage'));
const CallbackPage = lazy(() => import('./features/account/pages/EmailChangeCallbackPage'));
const DashboardPage = lazy(() => import('./features/dashboard/pages/DashboardPage'));
const EmployeesPage = lazy(() => import('./features/employees/pages/EmployeesPage'));
const EmployeeDetailsPage = lazy(() => import('./features/employees/pages/EmployeeDetailsPage')); 
const AttendancePage = lazy(() => import('./features/attendance/pages/AttendancePage'));
const AttendanceDetailsPage =lazy(() => import('./features/attendance/pages/AttendanceDetailsPage'));
const SitesPage = lazy(() => import('./features/sites/pages/SitesPage'));
const SiteDetailsPage = lazy(() => import('./features/sites/pages/SiteDetailsPage'));
const DevicesPage = lazy(() => import('./features/devices/pages/DevicesPage'));
const DeviceDetailsPage = lazy(() => import('./features/devices/pages/DeviceDetailsPage'));
const SystemPage = lazy(() => import('./features/system/pages/SystemPage'));
const SystemLogsPage = lazy(() => import('./features/system/pages/SystemLogsPage'));
const RolesPage = lazy(() => import('./features/system/pages/RolesPage'));
const WorkScheduleTemplatePage = lazy(() => import('./features/system/pages/WorkScheduleTemplatePage'));
const WorkSchedulesPage = lazy(() => import('./features/system/pages/WorkSchedulesPage'));
const RegistrationsPage = lazy(() => import('./features/system/pages/RegistrationsPage'));

import { PrivateLayout, NoLayout, ToastContainer, ModalHost, ScrollToTop, Loading } from './shared/components';
import { RequireAuth } from './shared/components/auth/RequireAuth';
import { RequireGuest } from './shared/components/auth/RequireGuest';
import { RequirePermission } from './shared/components/auth/RequirePermission';
import {
  AuthInitializer, // auth initialization
} from './shared/components/auth';

function App() {
  const { toasts, removeToast } = useUIStore();

  return (
    <QueryClientProvider client={queryClient}>
      <Router>
        <AuthInitializer />
        <ScrollToTop />
        <div className="min-h-screen bg-gray-50">
          <Suspense fallback={<Loading messageKey="route.loadingPage" size="full" /> }>
            <Routes>

              <Route element={<RequireGuest><NoLayout /></RequireGuest>}>
                <Route path="/" element={<LoginPage />} />
              </Route>

              <Route element={<RequireAuth><PrivateLayout /></RequireAuth>}>
                <Route element={<RequirePermission permissions={['view-content']} showForbidden={true}><Outlet /></RequirePermission>}>
                  <Route path="/dashboard" element={<DashboardPage />} />
                  <Route path="/employees" element={<EmployeesPage />} />

                  <Route element={<RequirePermission permissions={['manage-users']} showForbidden={true}><Outlet /></RequirePermission>}>
                    <Route path="/employees/:employeeId" element={<EmployeeDetailsPage />} />
                  </Route>
                  
                  <Route path="/attendance" element={<AttendancePage />} />
                  <Route path="/attendance/:Details" element={<AttendanceDetailsPage/>} />
                  <Route path="/sites" element={<SitesPage />} />
                  <Route path="/sites/:siteId" element={<SiteDetailsPage />} />
                  <Route path="/devices" element={<DevicesPage />} />
                  <Route path="/devices/:deviceId" element={<DeviceDetailsPage />} />
                </Route>

                <Route element={<RequirePermission permissions={['manage-registrations', 'manage-roles', 'manage-work-schedule', 'manage-logs', 'manage-users']} showForbidden={true}><Outlet /></RequirePermission>}>
                  <Route path="/system" element={<SystemPage />} />

                  <Route element={<RequirePermission permissions={['manage-registrations']} showForbidden={true}><Outlet /></RequirePermission>}>
                    <Route path="/system/registrations" element={<RegistrationsPage />} />
                  </Route>

                  <Route element={<RequirePermission permissions={['manage-roles']} showForbidden={true}><Outlet /></RequirePermission>}>
                    <Route path="/system/roles" element={<RolesPage />} />
                  </Route>

                  <Route element={<RequirePermission permissions={['manage-work-schedule']} showForbidden={true}><Outlet /></RequirePermission>}>
                    <Route path="/system/work-schedules" element={<WorkSchedulesPage />} />
                    <Route path="/system/work-schedules/new" element={<WorkScheduleTemplatePage />} />
                  </Route>

                  <Route element={<RequirePermission permissions={['manage-logs']} showForbidden={true}><Outlet /></RequirePermission>}>
                    <Route path="/system/logs" element={<SystemLogsPage />} />
                  </Route>
                </Route>

                <Route path="/account" element={<AccountPage />} />
                <Route path="/settings" element={<SettingsPage />} />
                <Route path="/notifications" element={<NotificationsPage />} />
              </Route>

              <Route element={<NoLayout />}>
                <Route path="*" element={<NotFoundPage/>} />
              </Route>
            </Routes>
          </Suspense>

          <ToastContainer toasts={toasts} onClose={removeToast} />
          <ModalHost />
        </div>
      </Router>
    </QueryClientProvider>
  );
}

export default App;
